/**
 * Liquid Glass — SVG displacement-map refraction.
 *
 * Port of the technique from
 * https://codepen.io/Sis-the-builder/pen/GgjppJe (Liquid Glass Navbar).
 *
 * For each glass surface we generate a per-element SVG <filter> in a shared
 * <defs>, sized to the element. The filter chain:
 *  1. feGaussianBlur on SourceGraphic (the backdrop)        → `blurred`
 *  2. feImage(displacementMap)                              → `disp_map`
 *  3. feDisplacementMap (R/G channels)                      → `displaced`
 *  4. feColorMatrix saturate                                → `displaced_sat`
 *  5. feImage(specularMap)                                  → `spec_layer`
 *  6. feComposite(in) — mask spec to the displaced shape    → `spec_masked`
 *  7. feComponentTransfer — fade spec by specularOpacity    → `spec_faded`
 *  8. feBlend(spec_masked, displaced)                       → `with_sat`
 *  9. feBlend(spec_faded, with_sat)                         → final
 *
 * Keeping the specular *inside* the filter (rather than as a CSS background)
 * is what makes the rim look like a curved bezel rather than a flat overlay
 * pasted on top — `feComposite operator="in"` masks the highlight to the
 * displaced shape's bounds before it gets blended back.
 */

export interface LiquidGlassConfig {
  /** Glass slab thickness — controls how far the rim refracts. */
  glassThickness: number;
  /** Width of the curved bezel in pixels. */
  bezelWidth: number;
  /** Index of refraction. ~1.4 = glass / acrylic. */
  ior: number;
  /** Multiplier on the displacement scale. 1.0 = neutral. */
  scaleRatio: number;
  /** Pre-blur applied to the backdrop before refraction. 0–30. */
  blur: number;
  /** Opacity of the rim specular highlight. 0–3. */
  specularOpacity: number;
  /** Saturation multiplier applied after refraction. 0=greyscale, 1=neutral. */
  specularSat: number;
  /** Tint color as 'r,g,b' (no alpha). */
  tintColor: string;
  /** Tint opacity 0..1. */
  tintOpacity: number;
  /** Inner shadow as a CSS color. */
  innerShadow: string;
  /** Inner shadow blur radius. */
  innerShadowBlur: number;
  /** Inner shadow spread. */
  innerShadowSpread: number;
  /** When true, light wraps the whole rim instead of a directional sheen. */
  balancedSpecular: boolean;
}

/**
 * Argo baseline — codepen pipeline with two tunes from user feedback:
 *  - blur 1 → 3 (200% increase) so the bg behind the glass actually
 *    softens enough to read as glass, not as a transparent hole.
 *  - scaleRatio 1.0 → 1.3 so the displacement at the bezel is more
 *    pronounced and the iconic "lensing" bend is visible.
 * Tint, inner shadow, balanced specular all stay off — the rim comes
 * from the SVG specular composite, not a CSS halo.
 */
const DEFAULT_CONFIG: LiquidGlassConfig = {
  glassThickness: 80,
  bezelWidth: 40,
  ior: 1.4,
  scaleRatio: 1.3,
  blur: 3,
  specularOpacity: 0.6,
  specularSat: 0,
  tintColor: '255,255,255',
  tintOpacity: 0,
  innerShadow: 'rgba(255,255,255,0)',
  innerShadowBlur: 0,
  innerShadowSpread: 0,
  balancedSpecular: false,
};

/* ------------------------------------------------------------------ */
/* Refraction profile                                                 */
/* ------------------------------------------------------------------ */

/** A 0..1 surface profile that approximates a curved bezel. */
function surfaceFn(x: number) {
  return Math.pow(1 - Math.pow(1 - x, 4), 0.25);
}

/**
 * Compute the per-pixel displacement amount along the normal to the bezel
 * surface for the given glass thickness, bezel width, and IOR.
 */
function calcRefractionProfile(
  glassThickness: number,
  bezelWidth: number,
  ior: number,
  samples = 128,
): Float64Array {
  const eta = 1 / ior;
  function refract(nx: number, ny: number): [number, number] | null {
    const dot = ny;
    const k = 1 - eta * eta * (1 - dot * dot);
    if (k < 0) return null;
    const sq = Math.sqrt(k);
    return [-(eta * dot + sq) * nx, eta - (eta * dot + sq) * ny];
  }
  const p = new Float64Array(samples);
  for (let i = 0; i < samples; i++) {
    const x = i / samples;
    const y = surfaceFn(x);
    const dx = x < 1 ? 0.0001 : -0.0001;
    const y2 = surfaceFn(x + dx);
    const deriv = (y2 - y) / dx;
    const mag = Math.sqrt(deriv * deriv + 1);
    const ref = refract(-deriv / mag, -1 / mag);
    p[i] = ref ? ref[0] * ((y * bezelWidth + glassThickness) / ref[1]) : 0;
  }
  return p;
}

/* ------------------------------------------------------------------ */
/* Map generators                                                     */
/* ------------------------------------------------------------------ */

/**
 * Render the displacement map to a canvas and return a data URL.
 * R channel encodes X displacement, G channel encodes Y displacement.
 * (128 = no displacement; 0..255 = signed -1..+1 around 128.)
 */
function generateDisplacementMap(
  w: number,
  h: number,
  radius: number,
  bezelWidth: number,
  profile: Float64Array,
  maxDisp: number,
): string {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d')!;
  const img = ctx.createImageData(w, h);
  const d = img.data;
  // Fill neutral grey (no displacement) plus full alpha.
  for (let i = 0; i < d.length; i += 4) {
    d[i] = 128;
    d[i + 1] = 128;
    d[i + 2] = 0;
    d[i + 3] = 255;
  }
  const r = radius;
  const rSq = r * r;
  const r1Sq = (r + 1) ** 2;
  const rBSq = Math.max(r - bezelWidth, 0) ** 2;
  const wB = w - r * 2;
  const hB = h - r * 2;
  const S = profile.length;
  for (let y1 = 0; y1 < h; y1++) {
    for (let x1 = 0; x1 < w; x1++) {
      const x = x1 < r ? x1 - r : x1 >= w - r ? x1 - r - wB : 0;
      const y = y1 < r ? y1 - r : y1 >= h - r ? y1 - r - hB : 0;
      const dSq = x * x + y * y;
      if (dSq > r1Sq || dSq < rBSq) continue;
      const dist = Math.sqrt(dSq);
      const fromSide = r - dist;
      const op =
        dSq < rSq
          ? 1
          : 1 - (dist - Math.sqrt(rSq)) / (Math.sqrt(r1Sq) - Math.sqrt(rSq));
      if (op <= 0 || dist === 0) continue;
      const cos = x / dist;
      const sin = y / dist;
      const bi = Math.min(((fromSide / bezelWidth) * S) | 0, S - 1);
      const disp = profile[bi] || 0;
      const dX = (-cos * disp) / maxDisp;
      const dY = (-sin * disp) / maxDisp;
      const idx = (y1 * w + x1) * 4;
      d[idx] = (128 + dX * 127 * op + 0.5) | 0;
      d[idx + 1] = (128 + dY * 127 * op + 0.5) | 0;
    }
  }
  ctx.putImageData(img, 0, 0);
  return c.toDataURL();
}

/**
 * Render the rim specular highlight map. Brighter pixels get masked over
 * the refracted backdrop (via feComposite operator="in" inside the SVG
 * filter) to give the illusion of light hitting the bezel.
 *
 * Note: alpha is encoded as col*coeff*op (no extra opacityMultiplier);
 * the per-element opacity scaling happens later via feComponentTransfer
 * inside the SVG filter chain, which matches the codepen exactly.
 */
function generateSpecularMap(
  w: number,
  h: number,
  radius: number,
  bezelWidth: number,
  balanced: boolean,
): string {
  const angle = Math.PI / 3; // 60° — soft top-left key light
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d')!;
  const img = ctx.createImageData(w, h);
  const d = img.data;
  d.fill(0);
  const r = radius;
  const rSq = r * r;
  const r1Sq = (r + 1) ** 2;
  const rBSq = Math.max(r - bezelWidth, 0) ** 2;
  const wB = w - r * 2;
  const hB = h - r * 2;
  const sv = [Math.cos(angle), Math.sin(angle)];
  for (let y1 = 0; y1 < h; y1++) {
    for (let x1 = 0; x1 < w; x1++) {
      const x = x1 < r ? x1 - r : x1 >= w - r ? x1 - r - wB : 0;
      const y = y1 < r ? y1 - r : y1 >= h - r ? y1 - r - hB : 0;
      const dSq = x * x + y * y;
      if (dSq > r1Sq || dSq < rBSq) continue;
      const dist = Math.sqrt(dSq);
      const fromSide = r - dist;
      const op =
        dSq < rSq
          ? 1
          : 1 - (dist - Math.sqrt(rSq)) / (Math.sqrt(r1Sq) - Math.sqrt(rSq));
      if (op <= 0 || dist === 0) continue;
      const cos = x / dist;
      const sin = -y / dist;
      const dot = balanced ? 1 : Math.abs(cos * sv[0] + sin * sv[1]);
      const edge = Math.sqrt(Math.max(0, 1 - (1 - fromSide) ** 2));
      const coeff = dot * edge;
      const col = (255 * coeff) | 0;
      const alpha = (col * coeff * op) | 0;
      const idx = (y1 * w + x1) * 4;
      d[idx] = col;
      d[idx + 1] = col;
      d[idx + 2] = col;
      d[idx + 3] = alpha;
    }
  }
  ctx.putImageData(img, 0, 0);
  return c.toDataURL();
}

/* ------------------------------------------------------------------ */
/* SVG filter assembly                                                */
/* ------------------------------------------------------------------ */

const SVG_NS = 'http://www.w3.org/2000/svg';
const DEFS_ID = 'argo-lg-defs';

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number>,
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  return el;
}

function ensureDefs(): SVGDefsElement {
  let defs = document.getElementById(DEFS_ID) as SVGDefsElement | null;
  if (defs && defs.ownerDocument === document) return defs;
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('width', '0');
  svg.setAttribute('height', '0');
  svg.setAttribute('aria-hidden', 'true');
  svg.style.cssText =
    'position:fixed;top:0;left:0;width:0;height:0;pointer-events:none;z-index:-1;';
  defs = document.createElementNS(SVG_NS, 'defs') as SVGDefsElement;
  defs.id = DEFS_ID;
  svg.appendChild(defs);
  document.documentElement.appendChild(svg);
  return defs;
}

function buildFilter(
  id: string,
  w: number,
  h: number,
  radius: number,
  cfg: LiquidGlassConfig,
): SVGFilterElement {
  const bezel = Math.min(cfg.bezelWidth, radius - 1, Math.min(w, h) / 2 - 1);
  const profile = calcRefractionProfile(cfg.glassThickness, bezel, cfg.ior, 128);
  const maxDisp = Math.max(...Array.from(profile).map(Math.abs)) || 1;
  const dispUrl = generateDisplacementMap(w, h, radius, bezel, profile, maxDisp);
  const specUrl = generateSpecularMap(w, h, radius, bezel * 2.5, cfg.balancedSpecular);
  const scale = maxDisp * cfg.scaleRatio;
  // Filter region MUST equal the element bounds for our `filter:`-based
  // delivery. The codepen uses a 0.36 pad with `backdrop-filter:` — there
  // the filter region only controls how far back the filter looks for
  // backdrop pixels; output stays inside the element. With `filter:`,
  // the filter region IS the output region, so any extension paints the
  // displaced grid into the area beyond the panel (the "drop shadow of
  // grid" artifact). Always clamp to the element.
  const fx = 0;
  const fy = 0;
  const fw = w;
  const fh = h;

  const filter = svgEl('filter', {
    id,
    x: fx,
    y: fy,
    width: fw,
    height: fh,
    filterUnits: 'userSpaceOnUse',
    primitiveUnits: 'userSpaceOnUse',
    'color-interpolation-filters': 'sRGB',
  });

  const blur = svgEl('feGaussianBlur', {
    in: 'SourceGraphic',
    stdDeviation: cfg.blur,
    result: 'blurred',
  });
  const dispImg = svgEl('feImage', {
    href: dispUrl,
    x: 0,
    y: 0,
    width: w,
    height: h,
    result: 'disp_map',
    preserveAspectRatio: 'none',
  });
  const dispMap = svgEl('feDisplacementMap', {
    in: 'blurred',
    in2: 'disp_map',
    scale,
    xChannelSelector: 'R',
    yChannelSelector: 'G',
    result: 'displaced',
  });
  const sat = svgEl('feColorMatrix', {
    in: 'displaced',
    type: 'saturate',
    values: cfg.specularSat,
    result: 'displaced_sat',
  });
  const spec = svgEl('feImage', {
    href: specUrl,
    x: 0,
    y: 0,
    width: w,
    height: h,
    result: 'spec_layer',
    preserveAspectRatio: 'none',
  });
  // Mask the spec to where the displaced backdrop is solid — this is what
  // makes the rim follow the bezel curve instead of looking like a flat
  // overlay pasted on top.
  const comp = svgEl('feComposite', {
    in: 'displaced_sat',
    in2: 'spec_layer',
    operator: 'in',
    result: 'spec_masked',
  });
  // Fade the unmasked spec by specularOpacity for the additive sheen pass.
  const tr = svgEl('feComponentTransfer', {
    in: 'spec_layer',
    result: 'spec_faded',
  });
  tr.appendChild(svgEl('feFuncA', { type: 'linear', slope: cfg.specularOpacity }));
  // Two-stage blend: masked spec onto the refraction, then add the faded
  // sheen on top. Matches the codepen output exactly.
  const b1 = svgEl('feBlend', {
    in: 'spec_masked',
    in2: 'displaced',
    mode: 'normal',
    result: 'with_sat',
  });
  const b2 = svgEl('feBlend', {
    in: 'spec_faded',
    in2: 'with_sat',
    mode: 'normal',
  });

  filter.append(blur, dispImg, dispMap, sat, spec, comp, tr, b1, b2);
  return filter;
}

/* ------------------------------------------------------------------ */
/* Per-element manager                                                */
/* ------------------------------------------------------------------ */

export interface GlassHandle {
  update: () => void;
  destroy: () => void;
}

interface AttachOptions {
  /** Optional override for the radius used in the filter. Falls back to computed border-radius. */
  radius?: number;
  /** Debounce ms for ResizeObserver. Default 60. */
  debounceMs?: number;
  /** Callback whenever a new filter has been generated. */
  onApply?: () => void;
}

interface AttachLayers {
  /** The mirror layer — paints var(--page-bg) at viewport-fixed origin
   *  and receives `filter: url(#id)` for the displacement + specular. */
  mirror: HTMLElement;
  /** The tint layer (gets background color + inset shadow). */
  tint: HTMLElement;
}

/**
 * Attach a liquid-glass effect to `el`. The caller provides the two layer
 * divs (`mirror`, `tint`) so React stays in charge of the DOM tree. The
 * hook only mutates the layers' style + the shared SVG `<defs>`.
 *
 * The displacement is delivered via the standard `filter:` property on the
 * mirror layer, NOT `backdrop-filter:`. `filter:` is universally supported
 * (Chrome / Firefox / Safari / Edge) — `backdrop-filter: url()` is the
 * part Chromium parses but refuses to paint with feImage-based maps. The
 * mirror's own `background-image` (set via CSS to var(--page-bg)) is the
 * SourceGraphic the SVG filter operates on, so we don't need backdrop
 * access at all.
 *
 * Returns a handle with `update()` (force regenerate) and `destroy()` (cleanup).
 */
export function attachLiquidGlass(
  el: HTMLElement,
  layers: AttachLayers,
  configFn: () => LiquidGlassConfig,
  opts: AttachOptions = {},
): GlassHandle {
  const { debounceMs = 60, onApply } = opts;
  const { mirror, tint } = layers;

  if (getComputedStyle(el).position === 'static') el.style.position = 'relative';

  let filterId: string | null = null;
  let timer: number | null = null;
  let alignFrame: number | null = null;
  let scrollIdleTimer: number | null = null;

  /**
   * Snap the mirror's `background-position` to negate the panel's
   * current viewport offset. With `--page-bg-size` set in viewport units
   * (100vw 100vh for the radial/ink, 40px tile for the grid), this lines
   * the mirror's bg up pixel-for-pixel with the body's bg in viewport
   * coordinates — i.e. exactly the slice of the page that would otherwise
   * be visible behind the panel.
   *
   * This is the JS replacement for `background-attachment: fixed`, which
   * silently breaks when an element has `filter:` applied. Called on
   * scroll, resize, and after every rebuild.
   */
  function alignMirror() {
    const rect = el.getBoundingClientRect();
    mirror.style.backgroundPosition = `${-rect.left}px ${-rect.top}px`;
  }

  function alignMirrorRaf() {
    if (alignFrame !== null) return;
    alignFrame = requestAnimationFrame(() => {
      alignFrame = null;
      alignMirror();
    });
  }

  /**
   * Scroll handler — FROZEN during active scrolling. The SVG filter on the
   * mirror re-rasterizes every time `background-position` changes, so
   * re-aligning on every scroll frame (across 11-22 live panels) is what
   * makes scrolling janky. Instead we leave the mirror where it is while
   * the user scrolls (the filter output is unchanged → no re-raster) and
   * snap it back into alignment ~120ms after scrolling stops. The page bg
   * is a subtle dark gradient/grid, so the frozen slice is imperceptible
   * in motion; alignment is exact again the instant the gesture ends.
   */
  function onScroll() {
    if (scrollIdleTimer) window.clearTimeout(scrollIdleTimer);
    scrollIdleTimer = window.setTimeout(() => {
      scrollIdleTimer = null;
      alignMirrorRaf();
    }, 120);
  }

  function rebuild() {
    const defs = ensureDefs();
    const rect = el.getBoundingClientRect();
    const w = Math.round(el.offsetWidth || rect.width);
    const h = Math.round(el.offsetHeight || rect.height);
    if (w < 4 || h < 4) return;

    const cs = getComputedStyle(el);
    const dataR = parseFloat(el.getAttribute('data-radius') || '0');
    const cssR = parseFloat(cs.borderTopLeftRadius || '0');
    const r = Math.max(2, Math.min(opts.radius || dataR || cssR || 24, w / 2, h / 2));

    if (filterId) {
      const old = defs.querySelector(`#${filterId}`);
      if (old) old.remove();
    }
    const cfg = configFn();
    filterId = 'argo-lg-' + Math.random().toString(36).slice(2, 10);
    const filter = buildFilter(filterId, w, h, r, cfg);
    defs.appendChild(filter);

    // Mirror layer — paints `var(--page-bg)`, the same gradient stack
    // body uses. We snap its bg-position to negate the panel's viewport
    // offset (alignMirror, below) so the mirror lines up exactly with
    // the body bg, then run the SVG filter chain on it via `filter:
    // url(#id)`. SourceGraphic for the filter is the aligned gradient/
    // grid, so what the displacement bends matches what a transparent
    // window would otherwise reveal — the iconic liquid-glass lensing.
    mirror.style.borderRadius = r + 'px';
    mirror.style.filter = `url(#${filterId})`;
    (mirror.style as any).webkitFilter = `url(#${filterId})`;
    alignMirror();

    // Tint layer — flat color + inset shadow. Empty for the codepen
    // baseline; brand variants (magenta, hero, menu) opt into a tint.
    tint.style.borderRadius = r + 'px';
    tint.style.backgroundColor = `rgba(${cfg.tintColor}, ${cfg.tintOpacity})`;
    tint.style.boxShadow = `inset 0 0 ${cfg.innerShadowBlur}px ${cfg.innerShadowSpread}px ${cfg.innerShadow}`;
    tint.style.backgroundImage = '';

    onApply?.();
  }

  function schedule() {
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(rebuild, debounceMs);
  }

  const ro = new ResizeObserver(schedule);
  ro.observe(el);

  // Track viewport position for mirror alignment.
  // scroll: panel moves relative to viewport as the page scrolls — the
  //   mirror's bg-position must follow so the grid stays aligned with body.
  // resize: viewport size change can move the panel; same.
  // capture: true — the page scrolls inside the dedicated
  // .app-shell__scroll container now (not the document), and element
  // scroll events don't bubble. A capturing window listener still sees
  // them, so panel mirrors re-align once scrolling settles (onScroll is
  // debounced to scroll-end so the filter isn't re-rastered mid-gesture).
  window.addEventListener('scroll', onScroll, { passive: true, capture: true });
  window.addEventListener('resize', alignMirrorRaf, { passive: true });

  // Initial paint after layout settles
  requestAnimationFrame(rebuild);

  return {
    update: rebuild,
    destroy() {
      ro.disconnect();
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', alignMirrorRaf);
      if (alignFrame !== null) cancelAnimationFrame(alignFrame);
      if (timer) window.clearTimeout(timer);
      if (scrollIdleTimer) window.clearTimeout(scrollIdleTimer);
      if (filterId) {
        const defs = document.getElementById(DEFS_ID);
        defs?.querySelector(`#${filterId}`)?.remove();
      }
      // Layer divs are React-owned; just clear the inline styles we set.
      mirror.removeAttribute('style');
      tint.removeAttribute('style');
    },
  };
}

/* ------------------------------------------------------------------ */
/* Variant presets                                                    */
/* ------------------------------------------------------------------ */

export type GlassVariantKey = 'default' | 'subtle' | 'sharp' | 'hero' | 'magenta' | 'flat' | 'menu';

export const VARIANT_PRESETS: Record<GlassVariantKey, LiquidGlassConfig> = {
  // Codepen baseline — pure refraction. Use this for content panels.
  default: { ...DEFAULT_CONFIG },
  // Lighter refraction for nested / inline surfaces (rows, chips).
  subtle: {
    ...DEFAULT_CONFIG,
    glassThickness: 50,
    bezelWidth: 28,
    specularOpacity: 0.45,
  },
  sharp: { ...DEFAULT_CONFIG, bezelWidth: 24 },
  // Featured panel — heavier glass for a wider bend, balanced rim.
  // No tint or inner shadow; the rim is purely the SVG specular.
  hero: {
    ...DEFAULT_CONFIG,
    glassThickness: 110,
    bezelWidth: 56,
    scaleRatio: 1.4,
    specularOpacity: 0.7,
    balancedSpecular: true,
  },
  // Brand-accent panels (magenta-tinted glass).
  magenta: {
    ...DEFAULT_CONFIG,
    tintColor: '187,65,152',
    tintOpacity: 0.14,
    innerShadow: 'rgba(255,0,255,0.24)',
    innerShadowBlur: 22,
    specularOpacity: 0.7,
    balancedSpecular: true,
  },
  flat: {
    ...DEFAULT_CONFIG,
    glassThickness: 40,
    bezelWidth: 24,
    blur: 0,
    specularOpacity: 0.4,
  },
  // Top-menu shell + dropdown flyouts — wide bezel for a pronounced
  // edge bend. Blur matches DEFAULT_CONFIG (3) so menu items are
  // readable when the menu sits over high-frequency content. Balanced
  // rim light all the way around. No tint — the glass is meant to be
  // see-through, not a frosted card.
  menu: {
    ...DEFAULT_CONFIG,
    glassThickness: 100,
    bezelWidth: 56,
    scaleRatio: 1.4,
    specularOpacity: 0.75,
    balancedSpecular: true,
  },
};
